// ══════════════════════════════════════════════════════════════════
//  부품(gear) → 월드 훅
//
//  ■ 왜 별도 파일인가 — **훅과 값을 갈라 두기 위해서다.**
//    강화가 바꾸는 것은 값이고, 바꾸지 않는 것은 **월드의 무엇을 건드리는가**다.
//    「무엇을 건드리는가」는 world.js 에 고정으로 박고 값은 이 파일이 낸다.
//
//  ⚠⚠ 【2026-07-31】 **요령 카드 체계가 통째로 삭제됐다**(지팡이 삭제 — 사장님 결정
//    *"무기선택 없애. 지팡이 없앨거야"*). 이 파일에서 함께 지운 것:
//      EFFECTS 표(카드 → 훅) · resolveMods · rampStacks · fullStacks · modsFromShop ·
//      P_WEAPON/P_COMMON(제시율 상수)
//    물맷돌은 원래부터 카드가 아니라 **부품**이었으므로(`gearMods` 아래) 판정부는
//    아무것도 잃지 않는다 — 카드 스택 경로는 지팡이만 타고 있었다.
//    ⚠ 낮에서 밤으로 넘어오는 훅(`dayin.js`)은 그대로다 — 개·화염·부활 계열의
//      **훅 키 자체는 neutralMods 가 계약으로 들고 있다.** 카드가 죽어도 키는 산다.
//
//  ■ **값은 확정값이 아니다.** 사장님 지시: *"밸런스 패치는 마지막에 한번에 하자."*
// ══════════════════════════════════════════════════════════════════
import { UNIT, PASU } from './config.js';

// ── 중립값 ────────────────────────────────────────────────────────
// 카드가 하나도 없을 때의 월드. **판정부는 이 키들만 읽는다.**
export function neutralMods() {
  return {
    // 무기 공통
    dmgMul: 1,        // 무기 피해 배율            ← 무거운 돌 / 무쇠 고리 / 바위를 던진다
    slowMul: 1,       // 공격 **간격** 배율(>1 = 느려진다)
    // ── ⚠ 「오버킬 이월」 두 훅(`overkill`·`spread`)은 2026-07-30 에 **삭제됐다** ──
    //   충돌 2 — *"사장님 승인. 깨끗이 지운다."* 차돌의 `carry` 가 사라지면서
    //   이 기제의 정체(전 무기 기본 규칙 + 무거운 돌이 전파 수를 올린다)가 통째로
    //   갈 곳을 잃었다. 지운 자리: `gear.CARRY` · 아래 `gearMods` 두 줄 ·
    //   `EFFECTS.k_heavy/k_press` 의 `spread` 항 · `world.damageEnemy` 의 연쇄 루프.
    //   ⚠ `world.evoProg.overkill`(넘친 피해 누적)은 그때 **남겼다가** 【2026-07-31】
    //     같이 지웠다 — 그 수를 읽던 `cards.js` 결③ 진화가 카드 체계와 함께 사라졌다.
    //     지금 `world.evoProg` 는 **없다**(`world.js` 1629행).
    // 물맷돌
    spreadMul: 1,     // 부채꼴 확산각 배율        ← 결① −, 결② +
    hitPad: 0,        // 명중 판정 폭 가산(px)     ← 결②
    pierce: 0,        // 관통 체수                 ← 공통 바닥 + 가벼운 돌 고유 +1
    pierceInf: 0,     // 관통 무제한(진화 ①)
    pierceRamp: 0,    // 관통마다 다음 표적 피해 +  ← 가르는 돌
    shards: 0,        // 명중 시 파편 수           ← 깨지는 돌
    eliteBurst: 0,    // 정예·위협 **처치** 시 파편 ← 부딪혀 갈라진다
    projAdd: 0,       // 투사체 가산               ← 비가 된다
    landSplash: 0,    // 착탄 광역 반경(유닛)      ← 바위를 던진다
    rangeMul: 1,      // 사거리 배율 = 흡수 반경 동반 ← 팔을 크게 돌린다
    alignDmg: 0,      // 조준선 정렬 1체당 피해 +   ← 결③
    // 지팡이
    radiusMul: 1,     // 호 반경 배율              ← 결① / 한 바퀴
    arcMul: 1,        // 호 각 배율                ← 결① / 진화
    outerBonus: 0,    // 호 **바깥 절반** 상한 가산 ← 끝으로 훑는다
    crowdRate: 0,     // 호 안 1체당 간격 감소율    ← 결② / 멈추지 않는다
    crowdDmg: 0,      // 호 안 1체당 피해 +        ← 결③
    whiffCut: 1,      // 헛스윙 시 간격 배율        ← 헛손질을 아낀다
    backswing: 0,     // 되치기 피해 비율(0=없음)   ← 되치기
    shockEvery: 0,    // n스윙마다 충격파(0=없음)   ← 땅을 울린다 / 멀리까지 울린다
    shockRMul: 1,     // 충격파 반경 배율          ← 멀리까지 울린다
    // **밀쳐내기는 「내가 미는 힘」이다** — 하이에나가 나를 미는 ENEMY[].knock 이
    // 아니다. 그쪽을 올리면 카드가 벌점이 된다. 스윙이 맞은 적을 밀어낸다.
    swingKnock: 0,    // 스윙 넉백 거리(0=없음)    ← 밀쳐내기
    knockMul: 1,      // 그 거리의 배율            ← 밀쳐내기
    // 공용
    dogDps: 1, dogSpeed: 1, dogDetect: 1,
    pickupMul: 1, moveMul: 1, dmgTakenMul: 1,
    fire: 0,          // 화염 지대 중첩            ← 기름을 붓는다
    ember: 0,         // 처치 자리 화염            ← 불티
    standDmg: 0,      // 정지 유지 시 피해 +       ← 발을 멈춘다
    dogGuard: 0,      // 개 곁 피해 감소           ← 등을 맡긴다
    // ── 회전 광역 「도는 돌이 친다」 (부품 전용 · 카드 경로에서는 0) ──
    // 0 이면 world 가 판정을 아예 안 돈다 — 지팡이·카드 경로는 영향 0이다.
    orbitR: 0, orbitRate: 1, orbitDmg: 0,
    // 궤적 대역 두께(반경 대비). 0 = 원 전체가 같은 피해 ← 묵은 차돌만 ≠ 0
    orbitBand: 0,
    orbitTick: 0,     // 궤도 주기(초). 0 = `ORBIT.tick` 을 쓴다 ← 묵은 차돌만 ≠ 0
    orbitFull: 0,     // 1 = `ORBIT.dmgRatio` 를 안 탄다(던지지 않는 돌)
    orbitSweepEvery: 0, // 단단한 ◆ 안쪽 전량 휘쓸기 주기
    // 한 번에 몇 마리 — `ORBIT.cap` 에 곱하는 **배율**. 1 = 기본 난간(4) 그대로.
    //   ⚠ 묵은 차돌만 ≠ 1 이고, 묵은 세트 ●●●●● 에서 `Infinity`(난간 없음)가 온다.
    orbitCap: 1,
    // **던지지 않는 돌인가**(묵은 차돌). world 발사부가 이 값을 보고 투사체를 안 만든다
    meleeOnly: 0,
    // ══════════════════════════════════════════════════════════════
    //  돌 여섯의 **네 축** (부품 전용 · 카드 경로는 전부 중립)
    //  ⚠ **0/null 이면 world 가 그 판정을 통째로 건너뛴다** — 지팡이·카드 경로에
    //    영향이 정확히 0 이라야 이 파일 첫 주석의 벽이 유지된다.
    // ══════════════════════════════════════════════════════════════
    skips: 0,         // 물수제비 튐 횟수          ← 거친 납작한 돌
    skipGap: 0,       // 첫 튐까지의 거리(px)      ← 〃 (사거리 배율은 world 가 곱한다)
    skipDecay: 0,     // 등비. 다음 간격 = 이번 × 이것
    lineGap: 1,       // 발 간격 배율(1 평범, 0 붙는다) ← 성긴 고른 돌. **scene 채널**
    rateCapMul: 1,    // 연사 하드 상한 배수        ← 성긴 세트만 ≠ 1
    lineEchoEvery: 0, lineEchoDmg: 0, lineEchoDelay: 0, // 손에 익은 ◆ 덧선
    skipAnchorEvery: 0, skipAnchorMul: 1,              // 매끈한 ◆ 재탐색
    shardChain: 0,    // 조각이 다시 조각을 내는 대(代) ← 길든 세트
    shardFinalSeek: 0,// 거친 ◆ 마지막 조각 재조준
    splashAgain: 0,   // 터진 자리가 또 터지는 횟수  ← 굳은 세트
    splashCrownDmg: 0, splashCrownR: 1,                // 무거운 ◆ 마지막 바깥 고리
    // ⚠⚠ **구 `grow`·`keep` 훅은 삭제됐다**(차돌 재설계 2026-07-30). 차돌이 근접(궤도)이
    //   되면서 「던지면 필드에 남고 주우면 자란다」가 통째로 없어졌다. 차돌의 축은
    //   이제 아래 `orbitCap` 과 `orbitR`·`orbitRate` 셋으로만 나간다 — 새 훅이 안 든다.
    setId: null,      // 발동한 세트 id(없으면 null) — scene 이 궤도 이펙트에 쓴다
    setBig: 0,        // 크게(●●●●●) 이상인가(◆도 기존 큰 효과 유지)
    setDiamond: 0,    // 네 칸 모두 ◆인가
    setSeen: null,    // 「같은 수식어가 몇 칸인가」 — F. 막대 UI 용(발동 여부와 별개)
    // 화면 채널 — 카드 경로는 기본값(등급 1 · 보통 크기 · 기본 회전)
    tier: 1, stoneId: null,
    tierBySlot: { stone: 1, cradle: 1, cord: 1, loop: 1 }, diamondSlots: [],
    stoneSize: 1, spinMul: 1, orbitLook: 1, traits: {},
  };
}

// ══════════════════════════════════════════════════════════════════
//  부품(gear) → 같은 훅 표
//
//  ■ **반환 모양이 `neutralMods()` 와 정확히 같다.** 그래서 `world.js` 판정부는
//    카드가 부품으로 갈린 것을 **모른다** — 이 파일 첫 주석이 세워 둔 벽이 여기서
//    값을 한다(*"「무엇을 건드리는가」는 world.js 에 고정으로 박고…"*).
//
//  ■ 붙는 곳이 없어 **0으로 남는 훅**이 있다. 갈래·진화 전용이던 것들이며
//    부품 구조에는 대응물이 없다. 지어내서 채우지 않는다:
//      alignDmg(결③) · pierceInf/landSplash/projAdd(진화) · crowdRate/crowdDmg/
//      outerBonus/whiffCut/backswing/shockEvery(지팡이) · 개·화염 계열(낮 이관)
// ══════════════════════════════════════════════════════════════════
import { AIM_PAD_REF, resolveGear, aimPad } from './gear.js';

export function gearMods(gear) {
  const g = resolveGear(gear);
  const m = neutralMods();

  m.dmgMul   = g.dmgMul;
  m.rangeMul = g.rangeMul;
  m.spreadMul = g.spreadMul;
  // 손에 익은 돌의 무한 관통선은 최근접 한 점만 맞히는 좁은 탄이 아니라, 물량이
  // 늘수록 한 줄을 가르는 돌이다. 새 판정값을 만들지 않고 기존 명중 폭의 기준 눈금
  // AIM_PAD_REF 하나만 더한다. 다른 돌과 위협/보스 조준 우선순위는 그대로다.
  m.hitPad   = aimPad(g.aimMul)
    + (g.stoneId === 'goreun' && g.pierceInf ? AIM_PAD_REF : 0);
  // resolveGear가 공통 `1 / 발 수`와 가벼운 돌 고유 +1을 합쳐 낸다.
  // 여기서 더하거나 다시 판정하지 않아 world.iPart가 같은 최종값을 받게 한다.
  m.pierce   = g.pierce;
  m.shards   = g.shards;
  // ⚠ 구 `m.pierceRamp = g.ramp` 삭제 — 차돌의 `ramp` 를 지웠으므로 **undefined 가
  //   새 나가고 있었다**(world 가 `?? 0` 으로 삼켜 크래시만 없었다). 「가르는 돌」 카드는
  //   여전히 `pierceRamp` 훅을 쓰므로 중립값(0)만 남긴다 — 부품이 그 값을 안 만들 뿐이다.
  // 무거운 돌 — **착탄 광역.** 구 진화 「바위를 던진다」가 쓰던 훅을 그대로 받는다.
  m.landSplash = g.splash;
  // 성긴 고른 돌 — **무한 관통.** 훅과 world 판정은 구 진화 「가르는 선」 자리에
  //   이미 있었고 **값만 도달하지 않고 있었다**(직전 단계 ⓐ). 그 한 줄이 이것이다.
  m.pierceInf  = g.pierceInf;
  // 거친 납작한 돌 — **물수제비.** ⚠ 튐마다 광역이 붙지 않는다(반경 값이 없다).
  m.skips = g.skips; m.skipGap = g.skipGap; m.skipDecay = g.skipDecay;
  m.skipSeek = g.skipSeek;
  // 세트 — world 가 읽는 둘(조각 연쇄 · 재폭발)과 scene 이 읽는 셋
  m.shardChain = g.shardChain; m.splashAgain = g.splashAgain;
  m.lineGap = g.lineGap; m.rateCapMul = g.rateCapMul;
  m.lineEchoEvery = g.lineEchoEvery; m.lineEchoDmg = g.lineEchoDmg; m.lineEchoDelay = g.lineEchoDelay;
  m.skipAnchorEvery = g.skipAnchorEvery; m.skipAnchorMul = g.skipAnchorMul;
  m.shardFinalSeek = g.shardFinalSeek;
  m.splashCrownDmg = g.splashCrownDmg; m.splashCrownR = g.splashCrownR;
  m.orbitSweepEvery = g.orbitSweepEvery;
  m.setId = g.set?.id ?? null;
  m.setBig = g.set && g.set.level !== 'small' ? 1 : 0;
  m.setDiamond = g.set?.level === 'diamond' ? 1 : 0;
  m.setSeen = g.setSeen;
  // 회전 광역 「도는 돌이 친다」 — world 가 네 값을 읽는다.
  //   ⚠ **`orbitCap` 이 묵은 차돌의 축이다**(gear.ORB). 반경만 키우고 상한을 안 풀면
  //     여전히 4마리만 쳐서 「히트박스가 커진 의미」가 사라진다 — 실측 +68% 자리.
  m.orbitR = g.orbitR; m.orbitRate = g.orbitRate; m.orbitDmg = g.orbitDmg;
  m.orbitCap = g.orbitCap; m.meleeOnly = g.meleeOnly ? 1 : 0; m.orbitBand = g.orbitBand;
  m.orbitTick = g.orbitTick; m.orbitFull = g.orbitFull;
  // 화면 채널 — scene 이 읽는다(판정 아님)
  m.tier = g.tier; m.stoneId = g.stoneId;
  m.tierBySlot = g.tierBySlot; m.diamondSlots = g.diamondSlots;
  m.stoneSize = g.stoneSize; m.spinMul = g.spinMul; m.orbitLook = g.orbitLook;
  // 부가 줄(●●●○○부터) — world 가 발사·피해·조각에서 읽는다
  m.traits = g.traits;

  // **빨라지는 쪽과 느려지는 쪽을 갈라서 보낸다.**
  //   world 주석의 규율: *"「무거운 돌」의 간격 +15%는 벌점이므로 상한(0.25초)을 다시
  //   풀지 않는다 — weaponStats 가 자른 뒤에 곱한다."*
  //   → 빨라지는 몫은 `weaponStats` 안쪽(상한 적용), 느려지는 몫은 `slowMul`(상한 밖).
  m.slowMul = g.rateMul >= 1 ? 1 : 1 / g.rateMul;

  // 부품 해석 원본 — `weaponStats` 가 빨라지는 몫과 발 수를 읽어야 한다.
  m._gear = g;
  return m;
}

// 부품 빌드에서 무기 스탯을 낸다. `weaponStats` 의 물맷돌 분기를 대체한다.
//   rate  : 빨라지는 몫만 여기서(하드 상한 4발/초 안), 느려지는 몫은 slowMul 로
//   range : 최종 사거리. base × 현재 축 × (장비 × 낮)이며 각 출처를 여기서 한 번만 곱한다
//   projectiles : 받침이 무는 개수. 구 `sAlt`(양손 던지기) 자리를 그대로 받는다
// ⚠ **`rateCapMul` 은 성긴 세트에서만 1 이 아니다.** 「간격이 사라진다」를 판정으로
//   옮기는 유일한 자리이며(gear.resolveGear 주석), 평시에는 하드 상한이 한 톨도
//   안 풀린다. 세트는 회차에 한 번 볼까 말까 한 사건이다.
export function gearWeaponStats(m, base, ax) {
  const g = m._gear;
  const up = Math.max(1, g?.rateMul ?? 1);
  // ⚠ **훅(`m.rateCapMul`)에서 읽는다. `_gear` 에서 읽지 않는다.**
  //   둘 다 같은 값을 들고 있어서 어느 쪽이든 돌기는 한다 — 그래서 위험하다.
  //   `_gear` 에서 읽으면 `neutralMods.rateCapMul` 이 **아무도 안 읽는 값**이 되고,
  //   그것이 규율 5 가 금지한 「배선만 있고 아무 일도 안 하는 값」이다.
  //   훅이 인터페이스이고 `_gear` 는 발 수·연사처럼 훅에 대응물이 없는 것만의 탈출구다.
  const cap = (1 / 0.25) * (m.rateCapMul ?? 1);
  return {
    rate: Math.min(cap, base.rate * ax * up),
    projectiles: g?.shots ?? 1,
    // `mergeDayIn` 뒤의 m.rangeMul 이 이미 장비×낮이다. `_gear.rangeMul` 을 다시 읽으면
    // 장비 배율만 제곱되므로, 조준·비행·표시가 함께 읽는 최종값을 여기서 완성한다.
    range: base.range * ax * (m.rangeMul ?? 1),
    sRate: 0, sAlt: 0,
  };
}

// ── 충격파 ────────────────────────────────────────────────────────
// 정본(명세 576행): 「4스윙마다 **반경 내 전체** 10뎀 충격파」. 고정 10 — 뎀 배율이 안 탄다.
// ⚠ **`SHOCK_DMG`(충격파 피해)가 여기 있었다** 【폐기 2026-08-07】. 명세 정본 값인데
//   **읽는 실행 코드가 하나도 없었다** — world.js 가 import 만 하고 안 썼다.
//   지팡이(충격파의 주인)가 삭제되며 남은 값이다. 「고친 값이 실행 코드에 안 닿는다」의 전형.

// ── 화염 지대 ─────────────────────────────────────────────────────
// 효과는 정본이다(명세 375행): **반경 내 초당 5, 합산 상한 12.**
//
// **배치 규칙은 정본에 없다.** 명세 483행이 *"미리 깔아 두면 곰이 지나며 맞고"* 라고
// 쓰는데, 이 게임의 입력은 **이동 하나**다(2-1). 능동 설치 버튼이 없으므로
// "깔아 둔다"가 성립하는 유일한 읽기는 **지나온 자리에 남는다**는 것이다.
// 그래서 자취로 구현하고, 그 사실을 여기 적어 둔다 — 읽은 것이지 적힌 것이 아니다.
export const FIRE = {
  dps: 5,               // 정본
  capDps: 12,           // 정본 — 겹쳐도 초당 12를 넘지 않는다
  radius: 4 * UNIT,     // 측정 대기
  life: 6.0,            // 측정 대기
  interval: 2.0,        // 측정 대기 — 중첩 1일 때 자취 주기(초)
  intervalPerStack: 0.75,
  maxZones: 12,         // 성능 보호용 상한이지 규칙이 아니다
  emberLife: 2.2,       // 「불티」가 남기는 불의 수명 — 측정 대기
  emberR: 2.0 * UNIT,   // 측정 대기
};

// ── 파편 ──────────────────────────────────────────────────────────
// 정본(명세 575행): 「파편은 **고정 10뎀 — 물량층 1발**, 정예·위협에는 발사체 뎀의 30%」
// 방향은 정본에 없다 — 명중점에서 **방사형**으로 흩는다(가장 단순한 읽기).
// ⚠ **「고정 10뎀」을 뒤집는다** — 그 조항은 「깨지는 돌」 카드 시절 것이고
//   **그때는 발이 1개였다.** 지금은 잔돌+그물이 12발이라, 고정 피해가
//   **약한 돌의 약점을 통째로 상쇄한다**: 잔돌은 본체 5뎀이라 물량층(HP 10)을
//   못 죽이는 것이 그 선택의 대가였는데, 파편이 10뎀이라 죽여 버렸다.
//   → **파편은 그 돌의 조각이다. 약한 돌은 약한 조각이 나온다.**
//     그러면 모난 돌(파편 담당)만 파편으로 물량층을 1발에 지운다 — 정체성이 산다.
export const SHARD = {
  flat: 10, ratio: 0.80, eliteMul: 0.30,
  speed: 380, life: 0.22,
  // 무거운 돌의 착탄 반경이 클수록 조각도 더 멀리 뻗는다. 속도와 수명을
  // 함께 제곱근 배율로 늘려 최종 비산 반경만 자연스럽게 확대한다.
  heavyReachPerSplash: 0.20,
  heavyReachCap: 1.85,
};
export function shardDamage(kind, srcDmg) {
  const base = (srcDmg ?? PASU.dmg) * SHARD.ratio;
  return kind === 'swarm' ? base : base * SHARD.eliteMul / 0.80;
}

// ⚠ **`SNARE` 상수가 여기 있었다** 【폐기 2026-08-07 — 사장님 지시】. 올무라는
// 물건 자체가 없어졌다(world.js 의 같은 날 주석 참조).

// ── 조각 흡인 ─────────────────────────────────────────────────────
// 반경에 들면 끌려오기 시작하고, **몸에 닿아야** 들어간다.
// 판정 시점이 「반경 진입」에서 「접촉」으로 옮겨 가지만 거리가 짧아 밤 총량은 안 흔들린다.
// ── 필드 드랍 「그러모으기」 ───────────────────────────────────────
// **뱀서류 최고의 카타르시스는 「전부 빨려오는 순간」이다.**
//   *"watching potentially thousands of experience gems streak towards you from
//     off-screen, triggering rapid-fire level-ups"* — VS 분석이 공통으로 짚는 자리.
// 우리 밤 12분에는 **좋은 일이 상점이 열릴 때뿐**이었다. 그 사이는 그냥 걷는다.
// 카드 「그러모으기」(치즈 흡수 반경 +40%)를 **필드 드랍으로 되살린다** — 새 개념이
// 아니라 정본에 있던 이름이고, 낮으로 이관될 뻔한 것을 밤의 사건으로 돌린다.
export const GATHER = {
  chance: 0.0016,     // 처치당 드랍 확률 — 12분에 두세 번. **드물어야 사건이다**
  r: 1.4 * UNIT,      // 주우려면 닿아야 한다
  life: 14,           // 안 주우면 사라진다 — 「지금 가야 하나」가 생긴다
  pullSec: 1.6,       // 발동 후 이 시간 동안 **화면 전체**가 빨려온다
  pullV: 1400,        // 그때의 속도 — 평소(260~1160)보다 확실히 빠르다
  // ⚠ **그 자리에서 조각이 터진다.** 「빨아들이기」만으로는 아무 일도 안 일어났다 —
  //   우리 흡수 반경(9유닛)이 커서 **바닥에 쌓이는 조각이 없기 때문**이다.
  //   VS 는 반경이 작아 젬이 깔리고 자석이 그걸 한꺼번에 준다. 그 전제가 우리엔 없다.
  //   → 흡수 반경을 줄이면 경제가 통째로 흔들리므로(사거리 = 흡수 반경),
  //     **드랍이 조각을 만들어 낸다.** VS 의 상자와 같은 문법이고 경제는 안 건드린다.
  burst: 26,          // 터지는 조각 수
  burstV: 420,        // 사방으로 흩어지는 속도 — 화면이 조각으로 덮인다
  needMul: 1.7,       // 총량 = 그 시점 levelNeed × 이것 → **한 번에 1~2단계 오른다**
};

export const PIECE_PULL = {
  base: 260,        // 끌리기 시작할 때의 속도(px/초) — 측정 대기
  gain: 900,        // 가까울수록 더한다. 마지막의 «톡» 이 여기서 난다 — 측정 대기
  eat: 16,          // 실제로 먹는 거리(px). 플레이어 몸 반지름 언저리
};

// ── 화면 흔들림 ───────────────────────────────────────────────────
// **장식이 아니라 신호다.** 조작을 뺏기는 순간(넉백)과 내가 크게 때린 순간(충격파)에만
// 흔든다. 아무 때나 흔들면 그때부터 흔들림은 정보가 아니라 소음이다.
// ⚠ **대량 처치에서는 상한이 필요하다.**
//   *"If players destroy dozens of enemies at once, overwhelming screen shake should
//     be avoided in favor of a nice consistent rumble"* — 게임 필 실무 통설.
//   지금 정예 처치·착탄 광역·회전 타격이 각각 흔들림을 건다. 연쇄로 겹치면
//   **화면이 흔들려서 적이 안 보인다.** 큰 사건(넉백)만 최대치를 쓰고 나머지는 눌린다.
export const SHAKE = { knock: 1.0, shock: 0.55, decay: 0.22, px: 9, cap: 0.62 };

// ── 피 · 시체 ─────────────────────────────────────────────────────
// **목업의 인상 절반이 바닥에 남는 자국이다.** 다만 목업은 적 4~5마리 화면이고
// 지금은 동시 **270**이다 — 상한이 없으면 밤 후반에 바닥이 통째로 붉어진다.
// 층으로 확률을 갈라, 큰 놈은 거의 남기고 물량층은 드물게 남긴다.
export const BLOOD = {
  pSwarm: 0.14, pElite: 0.9,      // 남길 확률 — 측정 대기(육안)
  cap: 90, life: 26,              // 얼룩 상한 · 수명(초)
  corpseCap: 24, corpseLife: 2.6, // 시체는 짧게 — 영구히 두면 화면을 덮는다
};

// ── 뜨는 피해 숫자 ────────────────────────────────────────────────
// 물량층까지 띄우되 작은 크기와 낮은 알파로 계층화한다. 상한이 밀집 구간을 방어하고,
// 정예·보스 숫자는 크기와 밝기로 전면에 남는다.
// 오래 남기는 것보다 **최신 타격이 항상 보이는 것**이 중요하다.
export const DMGTEXT = { on: true, cap: 46, life: 0.55, rise: 34 };

// ── 플레이어 바닥 광원 ────────────────────────────────────────────
// 목업에서 소년 발밑이 따뜻하게 빛나고 불티가 흩어진다. 밤이라 대비가 크다.
export const HEARTH = { r: 5.2 * UNIT, embers: 14, drift: 26, life: 1.5 };
